import React, { useRef, useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
// Removed Input component in favor of native textarea
import { SendHorizonal, Upload, Bot, Sparkles, Loader2, MessageSquare, Zap } from 'lucide-react';
import { LoadingIcon } from '@/components/ui/loading-icon';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { FileUploadDialog } from '@/components/upload/FileUploadDialog';
import { ProcessedFile, formatFileSize } from '@/lib/fileUploadUtils';
import { useUpload } from '@/contexts/UploadContext';
import { getModelDisplayName, getBadgeDisplayName } from '@/services/modelConfigService';

// Enable model information display based on environment
const SHOW_MODEL_USED = process.env.VITE_SHOW_MODEL_USED !== 'false';
const DEFAULT_MODEL = process.env.VITE_DEFAULT_MODEL || 'Llama 4';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  model_used?: string;
  error?: boolean;
  metadata?: {
    model_used?: string;
    rag_sources_used?: string[];
    [key: string]: any;
  };
}

interface TraderChatDashboardProps {
  messages: Message[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isLoading: boolean;
  handleSendMessage: (e: React.FormEvent) => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  activeRole?: string | null;
}

export const TraderChatDashboard = ({
  messages,
  inputMessage,
  setInputMessage,
  isLoading,
  handleSendMessage,
  isExpanded,
  setIsExpanded,
  activeRole,
}: TraderChatDashboardProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Auto-resize input textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [currentModelName, setCurrentModelName] = useState(DEFAULT_MODEL);
  const { addUploadedFiles } = useUpload();
  
  // Load current model configuration on mount
  useEffect(() => {
    const loadModelConfig = async () => {
      try {
        const { getCurrentModelConfig } = await import('@/services/modelConfigService');
        const config = await getCurrentModelConfig();
        setCurrentModelName(config.displayName);
      } catch (error) {
        console.warn('Failed to load model config for badge:', error);
      }
    };
    
    loadModelConfig();
  }, []);
  
  // Auto-scroll to bottom when new messages arrive with padding
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      
      // Add padding to ensure we don't scroll too far
      const container = messagesEndRef.current.closest('.overflow-y-auto');
      if (container) {
        (container as HTMLElement).style.paddingBottom = '24px';
      }
    }
  }, [messages]);
  
  // Adjust textarea height on content change
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [inputMessage]);

  const formatTimestamp = (timestamp: Date | undefined) => {
    if (!timestamp) {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    }
    return timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };
  
  // Check if the message has financial metadata
  const hasFinancialInfo = (message: Message) => {
    return message.metadata && 
      (message.metadata.model_used || message.metadata.rag_sources_used);
  };
  
  // Handle quick action buttons
  const handleQuickAction = (message: string) => {
    setInputMessage(message);
    const event = { preventDefault: () => {} } as React.FormEvent;
    handleSendMessage(event);
  };

  // Handle file upload completion
  const handleFilesProcessed = (files: ProcessedFile[]) => {
    addUploadedFiles(files);
    
    // Instead of adding to input message, we'll add file info as a user-friendly message
    const fileInfo = files.map(file => 
      `📎 Uploaded: ${file.name} (${formatFileSize(file.size)})`
    ).join('\n');
    
    const currentMessage = inputMessage;
    const newMessage = currentMessage ? `${currentMessage}\n\n${fileInfo}` : fileInfo;
    setInputMessage(newMessage);
  };

  // Handle upload button click
  const handleUploadClick = () => {
    setShowFileUpload(true);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden flex flex-col max-w-4xl mx-auto">
      {/* Enhanced Header with Premium Design */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
              <MessageSquare className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Stewie Trader AI
              </h2>
              <p className="text-sm text-blue-600 font-medium">Advanced Trading Intelligence</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {SHOW_MODEL_USED && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="bg-white/50 border-blue-200 text-blue-700 font-medium">
                      <Zap className="h-3 w-3 mr-1" />
                      {currentModelName}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Powered by {currentModelName} for financial intelligence</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
      
      {/* Enhanced Disclaimer with Premium Styling */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100">
        <div className="p-4 mx-4 my-3 bg-white/70 backdrop-blur-sm rounded-lg border border-blue-200/50">
          <div className="flex items-start gap-3">
            <div className="p-1 bg-blue-100 rounded-md mt-0.5">
              <Sparkles className="h-3 w-3 text-blue-600" />
            </div>
            <p className="text-sm text-blue-800 leading-relaxed">
              <span className="font-semibold">AI-Powered Trading Insights:</span> Advanced analysis and recommendations. 
              Validate all strategies with your financial advisor. Past performance doesn't guarantee future results.
            </p>
          </div>
        </div>
      </div>
      
      {/* Enhanced Messages Area */}
      <div className={`flex-1 bg-gradient-to-b from-gray-50/30 to-white ${messages.length > 0 ? 'pb-4' : ''}`}>
        {messages.length > 0 && (
          <div className="overflow-y-auto max-h-[400px] px-4 py-4">
            {messages.map((message, index) => (
              <div 
                key={message.id || index} 
                className={`mb-6 ${message.role === 'user' ? 'text-right' : 'text-left'}`}
              >
                <div
                  className={`inline-block max-w-[75%] ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg rounded-2xl px-5 py-3'
                      : hasFinancialInfo(message)
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 border border-blue-200 shadow-md rounded-2xl px-5 py-4'
                        : message.error
                          ? 'bg-gradient-to-r from-red-50 to-pink-50 text-red-800 border border-red-200 shadow-md rounded-2xl px-5 py-4'
                          : 'bg-white border border-gray-200 text-gray-800 shadow-md rounded-2xl px-5 py-4'
                  }`}
                >
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center shadow-sm
                        ${hasFinancialInfo(message) 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white' 
                          : message.error
                            ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                            : 'bg-gradient-to-r from-gray-400 to-gray-500 text-white'}`
                      }>
                        {hasFinancialInfo(message) 
                          ? <Sparkles className="h-4 w-4" /> 
                          : <Bot className="h-4 w-4" />
                        }
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700">
                          {hasFinancialInfo(message) 
                            ? 'Financial Analysis Engine' 
                            : 'Stewie AI Assistant'
                          }
                        </span>
                        
                        {(hasFinancialInfo(message) || message.model_used) && SHOW_MODEL_USED && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="outline" className="text-xs bg-white/50 border-blue-200 text-blue-700">
                                  {getModelDisplayName(message.metadata?.model_used || message.model_used)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Response generated using {message.metadata?.model_used || message.model_used}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="text-sm break-words whitespace-pre-wrap leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, inline, className, children, ...props }: {
                          node?: any;
                          inline?: boolean;
                          className?: string;
                          children: React.ReactNode;
                          [key: string]: any;
                        }) {
                          return inline ? (
                            <code className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-mono text-xs" {...props}>{children}</code>
                          ) : (
                            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto my-3 border border-gray-700">
                              <code className="text-sm">{children}</code>
                            </pre>
                          );
                        },
                        a({ node, ...props }) {
                          return <a className="text-blue-600 hover:text-blue-800 underline font-medium" {...props} />;
                        }
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                  
                  {hasFinancialInfo(message) && message.metadata && (
                    <div className="mt-3 pt-3 border-t border-blue-200/50">
                      <div className="bg-blue-100/50 rounded-lg p-3">
                        {message.metadata.rag_sources_used && message.metadata.rag_sources_used.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3 w-3 text-blue-600" />
                            <span className="text-xs font-medium text-blue-700">
                              Data Sources: {Array.isArray(message.metadata.rag_sources_used) 
                                ? message.metadata.rag_sources_used.join(', ') 
                                : message.metadata.rag_sources_used}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-2 px-2">
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            ))}
            
            {/* Enhanced Loading indicator */}
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl px-5 py-3 shadow-md">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                    <LoadingIcon size="sm" className="text-white" />
                  </div>
                  <span className="text-sm font-medium text-blue-700">Stewie is analyzing the markets...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Enhanced Input Area with Premium Design */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50/30 border-t border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="relative">
              <textarea
                ref={textareaRef}
                placeholder="Ask about trading strategies, market analysis, or portfolio optimization..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                rows={1}
                wrap="soft"
                disabled={isLoading}
                className="w-full rounded-xl border-2 border-gray-200 bg-white px-5 py-4 pr-20 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm placeholder-gray-500 shadow-sm"
                style={{ 
                  minHeight: '60px',
                  maxHeight: '120px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              
              {/* Action Buttons Container */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleUploadClick}
                        className="h-8 w-8 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 border-gray-300"
                      >
                        <Upload className="h-4 w-4 text-gray-600" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Upload files or data</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <Button
                  type="submit"
                  disabled={isLoading || !inputMessage.trim()}
                  className={`h-8 px-4 rounded-lg transition-all duration-200 ${
                    inputMessage.trim().length > 0 && !isLoading
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      <span className="text-sm">Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <SendHorizonal className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">Send</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleQuickAction("What's happening in the markets today?")}
                disabled={isLoading}
                className="text-xs bg-white/50 hover:bg-blue-50 border border-gray-200 rounded-lg text-gray-700 hover:text-blue-700 transition-all duration-200"
              >
                📊 Market Overview
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleQuickAction("Can you analyze my trading performance?")}
                disabled={isLoading}
                className="text-xs bg-white/50 hover:bg-blue-50 border border-gray-200 rounded-lg text-gray-700 hover:text-blue-700 transition-all duration-200"
              >
                📈 Performance Analysis
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleQuickAction("Suggest risk management strategies")}
                disabled={isLoading}
                className="text-xs bg-white/50 hover:bg-blue-50 border border-gray-200 rounded-lg text-gray-700 hover:text-blue-700 transition-all duration-200"
              >
                🛡️ Risk Management
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* File Upload Dialog */}
      <FileUploadDialog
        open={showFileUpload}
        onOpenChange={setShowFileUpload}
        onFilesProcessed={handleFilesProcessed}
        title="Upload Files to Trading Chat"
        description="Upload documents, spreadsheets, images, and code files to analyze with Stewie Trader AI."
      />
    </div>
  );
};
